import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import WorldMap from './WorldMap.jsx';
import { COMPACT_PANEL_QUERY } from './viewport.mjs';

const MIN_WIDTH = 260;
const DEFAULT_WIDTH = 460;
const COLLAPSED_WIDTH = 200;

/*
 * Whether the panel is a window or a sheet, live.
 *
 * Not a one-off read: a phone turned on its side crosses this breakpoint without
 * reloading, and that rotation is precisely the case where getting it wrong
 * leaves the submit button off the bottom of the stage.
 */
function useDockedPanel() {
  const [docked, setDocked] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COMPACT_PANEL_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia(COMPACT_PANEL_QUERY);
    const sync = () => setDocked(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  return docked;
}

/*
 * The guess panel floats over the sky rather than docking to an edge, so the sky
 * keeps the full frame and the map can be large without eating half the screen.
 * It opens top right, drags anywhere by its header, and resizes from the corner.
 *
 * The map is a fixed 2:1 viewBox, so height follows width and one number drives
 * the whole panel. The handle still reads both axes, since dragging a corner
 * diagonally is how anyone expects to resize a box.
 *
 * The grip sits bottom left because the panel is anchored top right: dragging
 * away from that corner, down and to the left, is the direction it actually
 * grows. Resizing therefore pins the right edge and moves the left one.
 */
export default function GuessPanel({ title, open, onToggle, children, mapProps }) {
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  const [position, setPosition] = useState(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const docked = useDockedPanel();

  useEffect(() => {
    const onMove = (event) => {
      const parent = panelRef.current?.parentElement.getBoundingClientRect();
      if (!parent) return;

      if (dragRef.current) {
        const { startX, startY, originX, originY } = dragRef.current;
        const self = panelRef.current.getBoundingClientRect();
        setPosition({
          x: Math.max(0, Math.min(parent.width - self.width, originX + event.clientX - startX)),
          y: Math.max(0, Math.min(parent.height - self.height, originY + event.clientY - startY))
        });
        return;
      }

      if (resizeRef.current) {
        const { startX, startY, startWidth, startLeft } = resizeRef.current;
        /*
         * Left and down grow it, right and up shrink it. Whichever axis moved
         * further wins: taking the larger signed value instead meant a purely
         * sideways drag was floored by the untouched vertical term, so the panel
         * would grow but never shrink. The map's 2:1 shape is why a vertical drag
         * counts double.
         */
        const byX = startX - event.clientX;
        const byY = (event.clientY - startY) * 2;
        const delta = Math.abs(byX) >= Math.abs(byY) ? byX : byY;

        // Bounded by the stage rather than by the gap beside the panel, which is
        // nothing when it opens against an edge.
        const next = Math.max(MIN_WIDTH, Math.min(parent.width - 24, startWidth + delta));
        setWidth(next);

        // Hold the right edge still, so the panel grows toward the grip.
        if (startLeft !== null) {
          setPosition((prev) =>
            prev ? { ...prev, x: Math.max(0, startLeft - (next - startWidth)) } : prev
          );
        }
      }
    };

    const onUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  /*
   * Collapsing returns it to the corner it started in.
   *
   * Clearing the dragged position hands the panel back to the stylesheet, which
   * anchors it top right. A collapsed panel is just a title bar, and leaving that
   * stranded wherever it was last dropped — halfway down the sky, or over the
   * horizon — reads as debris rather than a control. This way the expand button
   * is always in the same place.
   */
  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  /*
   * Pull the panel back into view whenever it grows.
   *
   * Once dragged it is pinned by its left edge, so expanding from collapsed, or
   * resizing near the right edge, pushes the far side off the stage where the
   * buttons cannot be reached. Anchoring by `right` instead would fix the expand
   * case and break dragging, so the clamp happens here after layout, when the
   * real width is known.
   */
  useLayoutEffect(() => {
    // A docked sheet is placed by the stylesheet and has no dragged position to
    // rescue; measuring one here would only fight the CSS that just placed it.
    if (docked || !position || !panelRef.current) return;
    const parent = panelRef.current.parentElement.getBoundingClientRect();
    const self = panelRef.current.getBoundingClientRect();

    const maxX = Math.max(0, parent.width - self.width);
    const maxY = Math.max(0, parent.height - self.height);
    if (position.x > maxX || position.y > maxY) {
      setPosition({ x: Math.min(position.x, maxX), y: Math.min(position.y, maxY) });
    }
  }, [open, width, position, docked]);

  const startDrag = (event) => {
    const parent = panelRef.current.parentElement.getBoundingClientRect();
    const self = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: self.left - parent.left,
      originY: self.top - parent.top
    };
  };

  const startResize = (event) => {
    event.stopPropagation();
    const self = panelRef.current.getBoundingClientRect();
    resizeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: self.width,
      startLeft: position ? position.x : null
    };
  };

  /*
   * Docked, the stylesheet owns the geometry entirely.
   *
   * This is the fix for the panel hanging off the side of a phone. The width was
   * being set inline, and an inline width beats any stylesheet rule that has not
   * shouted at it — so the `width: calc(100vw - 24px)` that was meant to rescue
   * small screens never applied, and even when it did it was measured against
   * the *viewport* while the panel is positioned inside the stage, which is
   * inset from it. Handing the whole geometry back to CSS at this size is
   * simpler than teaching the drag maths about two different layouts.
   */
  const style = docked ? undefined : { width: `${open ? width : COLLAPSED_WIDTH}px` };
  if (style && position) {
    style.left = `${position.x}px`;
    style.top = `${position.y}px`;
    style.right = 'auto';
  }

  return (
    <aside
      ref={panelRef}
      className={`sg-panel${open ? '' : ' is-collapsed'}${docked ? ' is-docked' : ''}`}
      style={style}
    >
      {/*
        Dragging and resizing are mouse affordances, and a docked sheet has
        nowhere to be dragged to. Leaving the grip and the corner handle on
        screen but inert would be worse than dropping them: an 18px resize
        target under a fingertip is a promise the layout cannot keep.
      */}
      <header className="sg-panel-head" onPointerDown={docked ? undefined : startDrag}>
        {!docked && (
          <span className="sg-panel-grip" aria-hidden="true">⠿</span>
        )}
        <span className="sg-panel-title">{title}</span>
        <button
          type="button"
          className="sg-panel-close"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? 'Collapse panel' : 'Expand panel'}
        >
          {open ? '–' : '+'}
        </button>
      </header>

      {open && (
        <>
          <div className="sg-panel-inner">
            <div className="sg-panel-map">
              <WorldMap {...mapProps} />
            </div>
            {children}
          </div>
          {!docked && (
            <span
              className="sg-panel-resize"
              onPointerDown={startResize}
              role="separator"
              aria-label="Resize panel"
            />
          )}
        </>
      )}
    </aside>
  );
}
